import { throttle } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';

import { mediaQueries } from '#@/shared/utils/styles';

export interface QueryMatches {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

const { matchMedia, addEventListener, removeEventListener } =
  typeof window !== 'undefined' ? window : {};

const calculateFirstActiveQuery = (...queries: string[]) => {
  let isActive = false;
  return queries.map((query) => {
    if (isActive) return false;

    isActive = matchMedia?.(query).matches ?? false;
    return isActive;
  });
};
const getMatches = () => {
  /*
   * Because we're using the mobile-first approach, where the queries don't
   * have both (lower and upper limits), we need to ensure that only the
   * largest query matching the current viewport size is consider active.
   */
  const [desktop, tablet, mobile] = calculateFirstActiveQuery(
    mediaQueries.desktop,
    mediaQueries.tablet,
    mediaQueries.mobile,
  );

  return { mobile, tablet, desktop };
};

const useQueryMatches = (): QueryMatches => {
  const [matches, setMatches] = useState(getMatches());

  // Weird thunk syntax due to a limitation in eslint-plugin-react-hooks
  const handleResize = useCallback(
    () =>
      throttle(() => {
        setMatches(getMatches());
      }, 500)(),
    [],
  );

  useEffect(() => {
    addEventListener?.('resize', handleResize);
    return () => removeEventListener?.('resize', handleResize);
  }, [handleResize]);

  return {
    isMobile: matches.mobile,
    isTablet: matches.tablet,
    isDesktop: matches.desktop,
  };
};

export default useQueryMatches;
